import React from 'react';
import { FiAlertCircle } from 'react-icons/fi';
import formatMoney from '../../utils/formatMoney';

/** Format integer cents for display. ERP APIs store money as integer cents. */
export function formatCents(cents, currency) {
  return formatMoney((Number(cents) || 0) / 100, currency || 'USD');
}

const STATUS_LABELS = {
  in_progress: 'In progress',
  on_hold: 'On hold',
  partially_paid: 'Partly paid',
  partially_received: 'Partly received',
};

export function statusLabel(status) {
  const raw = String(status || '').trim();
  if (!raw) return '';
  const key = raw.toLowerCase().replace(/[\s-]+/g, '_');
  if (STATUS_LABELS[key]) return STATUS_LABELS[key];
  const spaced = key.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function StatusBadge({ status, label }) {
  const key = String(status || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!key) return <span className="ui-muted">—</span>;
  return <span className={`svc-status svc-status--${key}`}>{label || statusLabel(key)}</span>;
}

export function errorMessage(error, fallback = 'Something went wrong.') {
  return error?.response?.data?.error || error?.message || fallback;
}

export function ErrorState({ children }) {
  return (
    <p className="ui-error" role="alert">
      <FiAlertCircle aria-hidden />
      <span>{children}</span>
    </p>
  );
}

export function EmptyState({ icon: Icon, title, hint, action, inline = false }) {
  return (
    <div className={`ui-empty${inline ? ' ui-empty--inline' : ''}`}>
      {Icon && <Icon className="ui-empty-icon" aria-hidden />}
      <p className="ui-empty-title">{title}</p>
      {hint && <p className="ui-empty-hint">{hint}</p>}
      {action && <div className="ui-empty-action">{action}</div>}
    </div>
  );
}

export function LoadingSkeleton({ rows = 4, label = 'Loading…' }) {
  return (
    <div className="ui-skeleton" role="status" aria-live="polite">
      <span className="visually-hidden">{label}</span>
      <span className="ui-skeleton-row ui-skeleton-row--head" aria-hidden />
      {Array.from({ length: rows }).map((_, i) => (
        <span key={i} className="ui-skeleton-row" aria-hidden />
      ))}
    </div>
  );
}

export function ListState({ query, empty, skeletonRows = 4, isEmpty, children }) {
  if (query?.isError) return <ErrorState>{errorMessage(query.error, 'Failed to load.')}</ErrorState>;
  if (query?.isLoading) return <LoadingSkeleton rows={skeletonRows} />;
  if (isEmpty) return empty;
  return children;
}
